import React, { useRef, useState, useEffect } from 'react';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import VersionControl from './VersionControl';
import VersionPreviewModal from './VersionPreviewModal';
import RubricSidebar from '../Shared/RubricSidebar';
import SubmissionHistorySidebar from '../Shared/SubmissionHistorySidebar';
import { useERDState } from './hooks/useERDState';
import { useResizing } from './hooks/useResizing';
import { useClass } from '../../utils/ClassContext';
import { lookupAssignment } from '../../utils/api/AssignmentsApi';

const ERDEditor = ({ username, apiKey, userRole, refreshUserStats, notificationCount, feedbackCount, quotaValue, isPublicView = false, assignmentId = null, componentId = null }) => {
  const containerRef = useRef(null);
  const { getClassContext } = useClass();
  // Main ERD editor - pass null for documentId and 'main' for source (no activity tracking here)
  const erdState = useERDState(username, apiKey, refreshUserStats, getClassContext, null, 'main');
  const resizing = useResizing(containerRef, erdState);

  const isInstructor = userRole === 'Instructor' || userRole === 'TA';

  // Version control sidebar state
  const [showVersionSidebar, setShowVersionSidebar] = useState(false);
  const [showRubricSidebar, setShowRubricSidebar] = useState(false);
  const [showSubmissionsSidebar, setShowSubmissionsSidebar] = useState(false);

  // Auto-detected assignment context
  const [detectedAssignmentId, setDetectedAssignmentId] = useState(null);
  const [detectedComponentId, setDetectedComponentId] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);
  const effectiveAssignmentId = assignmentId || detectedAssignmentId;
  const effectiveComponentId = componentId || detectedComponentId;

  // Auto-detect assignment when environment changes (always runs regardless of prop)
  useEffect(() => {
    const env = erdState.selectedEnvironment;
    if (!env || env === 'None') {
      setDetectedAssignmentId(null);
      setDetectedComponentId(null);
      setAssignmentData(null);
      return;
    }

    const classContext = getClassContext();
    if (!classContext) return;

    lookupAssignment({ environment_id: env }, classContext)
      .then(result => {
        if (result.success && result.assignment) {
          setDetectedAssignmentId(result.assignment.assignment_id);
          setAssignmentData(result.assignment);
          // Find ERD component
          const comp = (result.assignment.components || []).find(c => c.type === 'erd');
          setDetectedComponentId(comp?.component_id || null);
        } else {
          setDetectedAssignmentId(null);
          setDetectedComponentId(null);
          setAssignmentData(null);
        }
      })
      .catch(() => {
        setDetectedAssignmentId(null);
        setDetectedComponentId(null);
        setAssignmentData(null);
      });
  }, [erdState.selectedEnvironment]);
  const [sidebarAnimating, setSidebarAnimating] = useState(false);
  
  // Preview modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    filename: null
  });
  
  
  
  // Handle animated sidebar opening
  const openVersionSidebar = () => {
    setShowVersionSidebar(true);
    setSidebarAnimating(true);
    // Trigger animation on next frame
    requestAnimationFrame(() => {
      setSidebarAnimating(false);
    });
  };
  
  // Handle animated sidebar closing
  const closeVersionSidebar = () => {
    setSidebarAnimating(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setShowVersionSidebar(false);
      setSidebarAnimating(false);
    }, 300);
  };
  
  // Preview modal handlers
  const handlePreviewVersion = (filename) => {
    setPreviewModal({
      isOpen: true,
      filename: filename
    });
  };
  
  const handleClosePreview = () => {
    setPreviewModal({
      isOpen: false,
      filename: null
    });
  };
  
  const handleLoadFromPreview = (workData) => {
    erdState.handleVersionLoad(workData);
    handleClosePreview();
    closeVersionSidebar();
  };

  return (
    <div className="h-full bg-white rounded-lg shadow-lg flex flex-col">
      {/* Restore Notification */}
      {erdState.showRestoreNotification && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 m-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-blue-700 text-sm">
              ✅ Your previous work has been restored automatically!
            </p>
            <button
              onClick={() => erdState.setShowRestoreNotification(false)}
              className="text-blue-500 hover:text-blue-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main container with proper height management */}
      <div ref={containerRef} className="flex flex-1 min-h-0">
        {/* Left Panel - ERD Editor */}
        <LeftPanel
          erdState={erdState}
          resizing={resizing}
          username={username}
          apiBaseUrl="/api"
          userRole={userRole}
          onOpenVersionSidebar={openVersionSidebar}
          getClassContext={getClassContext}
          showRubric={!!effectiveAssignmentId}
          onOpenRubric={() => setShowRubricSidebar(true)}
          showSubmissions={!!effectiveAssignmentId && !isInstructor}
          onOpenSubmissions={() => setShowSubmissionsSidebar(true)}
          assignmentId={effectiveAssignmentId}
          componentId={effectiveComponentId}
          assignmentData={assignmentData}
        />

        {/* Horizontal Resize Handle */}
        <div
          className="w-2 bg-gray-300 hover:bg-gray-400 active:bg-gray-500 cursor-col-resize flex items-center justify-center transition-colors duration-200 group self-stretch"
          onMouseDown={resizing.handleHorizontalResizeStart}
        >
          <div className="w-1 h-16 bg-gray-500 rounded-full group-hover:bg-gray-600 transition-colors duration-200"></div>
        </div>

        {/* Right Panel - AI Assistant */}
        <RightPanel
          erdState={erdState}
          apiKey={apiKey}
          username={username}
          notificationCount={notificationCount}
          feedbackCount={feedbackCount}
          quotaValue={quotaValue}
          isPublicView={isPublicView}
          getClassContext={getClassContext}
        />
      </div>
      
      {/* Rubric Sidebar */}
      {effectiveAssignmentId && (
        <RubricSidebar
          assignmentId={effectiveAssignmentId}
          componentId={effectiveComponentId}
          classContext={getClassContext()}
          isOpen={showRubricSidebar}
          onClose={() => setShowRubricSidebar(false)}
          isInstructor={isInstructor}
        />
      )}

      {/* Submission History Sidebar */}
      {effectiveAssignmentId && (
        <SubmissionHistorySidebar
          assignmentId={effectiveAssignmentId}
          classContext={getClassContext()}
          username={username}
          isOpen={showSubmissionsSidebar}
          onClose={() => setShowSubmissionsSidebar(false)}
          submissionType="erd"
        />
      )}

      {/* Version Control Sidebar */}
      {showVersionSidebar && (
        <>
          {/* Overlay with fade animation */}
          <div
            className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
              sidebarAnimating ? 'bg-opacity-0' : 'bg-opacity-50'
            }`}
            onClick={closeVersionSidebar}
          />

          {/* Sidebar with slide animation */}
          <div className={`fixed right-0 top-0 h-full w-80 bg-white shadow-xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
            sidebarAnimating ? 'transform translate-x-full' : 'transform translate-x-0'
          }`}>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Version History</h3>
              <button
                onClick={closeVersionSidebar}
                className="text-gray-400 hover:text-gray-600 text-xl transition-colors duration-200"
              >
                ×
              </button>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <VersionControl
                username={username}
                selectedEnvironment={erdState.selectedEnvironment}
                onVersionLoad={erdState.handleVersionLoad}
                onSidebarClose={closeVersionSidebar}
                onPreviewVersion={handlePreviewVersion}
                getClassContext={getClassContext}
              />
            </div>
          </div>
          
          {/* Version Preview Modal - Separate from sidebar */}
          <VersionPreviewModal
            isOpen={previewModal.isOpen}
            onClose={handleClosePreview}
            username={username}
            environment={erdState.selectedEnvironment}
            filename={previewModal.filename}
            onLoadVersion={handleLoadFromPreview}
            getClassContext={getClassContext}
          />
        </>
      )}
    </div>
  );
};

export default ERDEditor;